import { deleteCookie, getCookie, setCookie } from "@/shared/infrastructure/storage/cookies";

export const ACCESS_TOKEN_KEY = "avizo_access_token";
export const REFRESH_TOKEN_KEY = "avizo_refresh_token";
export const USER_ID_KEY = "avizo_user_id";
export const SESSION_KEY = "avizo_session";
export const AUTH_CLEARED_EVENT = "avizo:auth-cleared";

export interface TokenPair {
  accessToken: string;
  refreshToken: string;
}

export interface StoredSession {
  userId: string;
  fullName: string;
  email: string | null;
  role: string;
  profilePicture?: string | null;
}

export function getAccessToken(): string | null {
  return getCookie(ACCESS_TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return getCookie(REFRESH_TOKEN_KEY);
}

export function getStoredUserId(): string | null {
  const cookieVal = getCookie(USER_ID_KEY);
  if (cookieVal) return cookieVal;
  return getStoredSession()?.userId ?? null;
}

export function getStoredSession(): StoredSession | null {
  const raw = getCookie(SESSION_KEY);
  if (!raw) return null;
  try {
    const session = JSON.parse(raw) as StoredSession;
    if (!session || typeof session !== "object" || Array.isArray(session) || typeof session.userId !== "string") {
      return null;
    }
    return session;
  } catch {
    return null;
  }
}

export function getStoredUserRole(): string | null {
  return getStoredSession()?.role ?? null;
}

export function setTokens({ accessToken, refreshToken }: TokenPair): void {
  setCookie(ACCESS_TOKEN_KEY, accessToken, 7);
  setCookie(REFRESH_TOKEN_KEY, refreshToken, 7);
}

export function setStoredUserId(userId: string): void {
  setCookie(USER_ID_KEY, userId, 7);
}

export function setStoredSession(session: StoredSession): void {
  setStoredUserId(session.userId);
  setCookie(SESSION_KEY, JSON.stringify(session), 7);
}

export function clearAuthStorage(): void {
  const baseKeys = [ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY, USER_ID_KEY, SESSION_KEY];
  for (const base of baseKeys) {
    deleteCookie(base);
    deleteCookie(`${base}_dev`);
    deleteCookie(`${base}_staging`);
  }

  if (typeof window !== "undefined") {
    try {
      window.dispatchEvent(new CustomEvent(AUTH_CLEARED_EVENT));
    } catch {
      // Ignore if event dispatching is unsupported in some environments
    }
  }
}

